import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from "react-simple-maps";
import { X } from "lucide-react";
import api from "../lib/api";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

export default function MapPage() {
  const { t } = useTranslation();
  const [traditions, setTraditions] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [selected, setSelected] = useState(null);
  const [region, setRegion] = useState("all");

  useEffect(() => {
    api.get("/map/traditions").then(({ data }) => setTraditions(data)).catch(() => {});
    api.get("/map/timeline").then(({ data }) => setTimeline(data)).catch(() => {});
  }, []);

  const regions = ["all", ...new Set(traditions.map((tr) => tr.region))];
  const visible = traditions.filter((tr) => region === "all" || tr.region === region);

  return (
    <main className="max-w-7xl mx-auto px-6 py-16 relative z-10" data-testid="map-page">
      <h1 className="text-4xl sm:text-5xl font-serif-display tracking-tight">{t("map.title")}</h1>
      <p className="text-foreground/65 mt-3">{t("map.subtitle")}</p>

      <div className="flex flex-wrap gap-2 mt-8">
        {regions.map((r) => (
          <button key={r} onClick={() => setRegion(r)}
            className={`text-xs px-4 py-1.5 rounded-full border transition-colors duration-200 ${region === r ? "border-primary bg-primary/10 text-primary" : "border-primary/20 text-foreground/60 hover:border-primary/40"}`}
            data-testid={`region-filter-${r === "all" ? "all" : r.replace(/\s/g, "-")}`}>
            {r === "all" ? t("map.allRegions") : r}
          </button>
        ))}
      </div>

      <div className="relative mt-8 bg-card border border-primary/15 rounded-lg overflow-hidden" data-testid="world-map">
        <ComposableMap projection="geoNaturalEarth1" style={{ width: "100%", height: "auto" }}>
          <ZoomableGroup center={[-20, 15]} zoom={1.4}>
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography key={geo.rsmKey} geography={geo}
                    fill="#EAF2E6" stroke="rgba(31,107,173,0.35)" strokeWidth={0.4}
                    style={{ default: { outline: "none" }, hover: { fill: "#DCE9F5", outline: "none" }, pressed: { outline: "none" } }} />
                ))
              }
            </Geographies>
            {visible.map((tr) => (
              <Marker key={tr.id} coordinates={tr.coordinates} onClick={() => setSelected(tr)}>
                <circle r={5} fill="#1F6BAD" stroke="#FFFFFF" strokeWidth={1.5} style={{ cursor: "pointer" }} data-testid={`map-marker-${tr.id}`} />
                <circle r={9} fill="none" stroke="#1F6BAD" strokeWidth={0.6} opacity={0.6} style={{ cursor: "pointer" }} onClick={() => setSelected(tr)} />
              </Marker>
            ))}
          </ZoomableGroup>
        </ComposableMap>

        {selected && (
          <div className="absolute top-4 right-4 w-80 max-w-[calc(100%-2rem)] bg-background/95 backdrop-blur-xl border border-primary/30 rounded-lg p-6 shadow-[0_8px_30px_rgba(0,0,0,0.6)]" data-testid="tradition-detail">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-primary">{selected.region} · {selected.period}</p>
                <h3 className="font-serif-display text-2xl mt-1">{selected.name}</h3>
                <p className="text-xs text-muted-foreground">{selected.country}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-muted-foreground hover:text-foreground" data-testid="tradition-close-btn">
                <X size={16} />
              </button>
            </div>
            <p className="text-sm text-foreground/75 mt-4 leading-relaxed">{selected.description}</p>
            {selected.related_courses?.length > 0 && (
              <div className="mt-4">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">{t("map.relatedCourses")}</p>
                <div className="flex flex-wrap gap-2">
                  {selected.related_courses.map((cid) => (
                    <Link key={cid} to={`/cours/${cid}`} className="text-xs px-3 py-1 rounded-full border border-primary/40 text-primary hover:bg-primary/10 transition-colors duration-200" data-testid={`related-course-${cid}`}>
                      {cid.toUpperCase()}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Timeline */}
      <h2 className="text-3xl font-serif-display tracking-tight mt-20 mb-10">{t("map.timeline")}</h2>
      <div className="relative border-l border-primary/25 ml-3 space-y-10 pb-4" data-testid="timeline">
        {timeline.map((ev, i) => (
          <div key={i} className="relative pl-8" data-testid={`timeline-event-${i}`}>
            <span className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full bg-primary" />
            <p className="font-serif-display text-2xl text-primary">{ev.year}</p>
            <p className="font-medium text-foreground mt-1">{ev.title}</p>
            <p className="text-sm text-foreground/60 mt-1 max-w-xl leading-relaxed">{ev.description}</p>
          </div>
        ))}
      </div>
    </main>
  );
}
